import React from 'react'
import { Box, Link, Text } from '@workwhile/ui'
import { DateWithRelativeTime } from 'components/Table/DateWithRelativeTime'
import { IssueSection } from './IssueSection'
import { useInvoiceIssuesContextValue } from '../context/InvoiceIssuesProvider'
import * as ROUTES from 'constants/routes'
import type { MissedInvoiceChargesIssue } from 'api/invoiceIssues/getInvoiceIssues'

function formatInvoiceCycle(issue: MissedInvoiceChargesIssue): string {
  if (issue.cycleType === 'weekly' && issue.weekStartDay != null) {
    const day =
      issue.weekStartDay.charAt(0).toUpperCase() + issue.weekStartDay.slice(1)
    return `Weekly (week starts ${day})`
  }
  if (issue.cycleType === 'weekly') {
    return 'Weekly'
  }
  if (issue.cycleType === 'standard') {
    return 'Standard'
  }
  const _exhaustive: never = issue.cycleType
  return _exhaustive
}

export const MissedInvoiceSection = () => {
  const {
    state: {
      data: { missedInvoiceCharges },
    },
  } = useInvoiceIssuesContextValue()

  return (
    <IssueSection
      title="Missed invoice charges"
      description="Companies with ready-to-bill charges that were left out when invoices were last generated for their billing schedule."
      emptyMessage="No companies with missed invoice charges."
      isEmpty={missedInvoiceCharges.length === 0}
    >
      {missedInvoiceCharges.map((issue) => (
        <Box
          key={issue.company.id}
          border="1px solid"
          borderColor="neutrals.100"
          borderRadius="small"
          p={3}
          mb={2}
        >
          <Text fontWeight="500" mb={1}>
            <Link
              to={`${ROUTES.CHARGES}?company=${issue.company.id}&invoiced=false`}
            >
              {issue.company.name}
            </Link>{' '}
            ({issue.company.id}) — {issue.charges.length} charge
            {issue.charges.length === 1 ? '' : 's'}
          </Text>
          <Text color="lightText" mb={1}>
            Schedule: {formatInvoiceCycle(issue)}
          </Text>
          <Text color="lightText" mb={1}>
            Last invoices generated:{' '}
            <DateWithRelativeTime
              date={issue.lastInvoiceRunAt}
              variant="inline"
            />
          </Text>
          {issue.charges.map((charge) => (
            <Text key={charge.id} color="lightText" ml={2}>
              Charge {charge.id}
              {charge.shiftId != null ? (
                <>
                  {' '}
                  · shift{' '}
                  <Link
                    to={ROUTES.SHIFT_DETAIL.replace(
                      ':shiftId',
                      String(charge.shiftId)
                    )}
                  >
                    {charge.shiftId}
                  </Link>
                </>
              ) : null}{' '}
              created{' '}
              <DateWithRelativeTime date={charge.createdAt} variant="inline" />
            </Text>
          ))}
        </Box>
      ))}
    </IssueSection>
  )
}
